import React, { useMemo } from "react";
import Dropdown from "./Dropdown";
import { SmallTrashIcon } from "../icons";
import S from "../styles";

export default function PrintingLineItemCard({ item, index, filament, onChange, onRemove, canRemove }) {
  // Cascading: material → brand → color
  const materialTypes = useMemo(() => [...new Set(filament.map((f) => f.material))].sort(), [filament]);

  const brands = useMemo(
    () => (item.material ? [...new Set(filament.filter((f) => f.material === item.material).map((f) => f.brand))].sort() : []),
    [filament, item.material]
  );

  const colors = useMemo(
    () =>
      item.material && item.brand
        ? [...new Set(filament.filter((f) => f.material === item.material && f.brand === item.brand).map((f) => f.color))].sort()
        : [],
    [filament, item.material, item.brand]
  );

  const matched = useMemo(
    () => filament.find((f) => f.material === item.material && f.brand === item.brand && f.color === item.color),
    [filament, item.material, item.brand, item.color]
  );

  const update = (field, value) => {
    const next = { ...item, [field]: value };
    if (field === "material") { next.brand = ""; next.color = ""; }
    if (field === "brand") { next.color = ""; }
    onChange(next);
  };

  // Cost: (weightGrams / 1000) * price per kg (matched.price is per spool weight)
  // Simplified: price per gram = matched.price / spoolWeightGrams
  // For now: cost = (weightGrams * partsNeeded) / 1000 * pricePerKg
  // Since filament price is per spool (e.g. $24.99 for 1kg), cost per gram = price / spoolGrams
  const spoolGrams = matched ? parseFloat(matched.weight) * (matched.weight.includes("kg") ? 1000 : matched.weight.includes("g") ? 1 : 1000) : 1000;
  const pricePerGram = matched ? matched.price / spoolGrams : 0;
  const weightPerPart = parseFloat(item.weightGrams) || 0;
  const partsNeeded = parseInt(item.partsNeeded) || 0;
  const totalWeight = weightPerPart * partsNeeded;
  const lineCost = matched ? totalWeight * pricePerGram : 0;
  const spoolsNeeded = spoolGrams > 0 && totalWeight > 0 ? Math.ceil(totalWeight / spoolGrams) : 0;

  return (
    <div style={S.lineCard}>
      <div style={S.lineCardHeader}>
        <span style={S.lineCardNumber}>Item {index + 1}</span>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {matched && lineCost > 0 && <span style={S.lineCardCost}>${lineCost.toFixed(2)}</span>}
          {canRemove && (
            <button onClick={onRemove} style={S.removeLineBtn} title="Remove item"><SmallTrashIcon /></button>
          )}
        </div>
      </div>
      <div style={S.lineCardBody}>
        <div className="selection-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "0 16px" }}>
          <Dropdown label="Filament Type" value={item.material} onChange={(v) => update("material", v)} options={materialTypes.map((t) => ({ value: t, label: t }))} placeholder="Choose type..." />
          <Dropdown label="Brand" value={item.brand} onChange={(v) => update("brand", v)} options={brands.map((b) => ({ value: b, label: b }))} placeholder="Choose brand..." disabled={!item.material} />
          <Dropdown label="Color" value={item.color} onChange={(v) => update("color", v)} options={colors.map((c) => ({ value: c, label: c }))} placeholder="Choose color..." disabled={!item.brand} />
        </div>
        {matched && (
          <div style={S.priceTag}>
            <span style={{ color: "rgba(10,10,15,0.58)", fontSize: 13, textTransform: "uppercase", letterSpacing: 1.5 }}>Spool Price ({matched.weight})</span>
            <span style={{ fontSize: 24, fontWeight: 700, color: "#0033FF", fontFamily: "'JetBrains Mono', monospace" }}>
              ${matched.price.toFixed(2)}
            </span>
          </div>
        )}
        <div style={S.paramsRow}>
          <div style={{ flex: 1 }}>
            <label style={S.label}>Weight Per Part (grams)</label>
            <input type="number" min="0" step="0.1" value={item.weightGrams || ""} onChange={(e) => update("weightGrams", e.target.value)} placeholder="e.g. 45" style={S.input} />
          </div>
          <div style={{ flex: 1 }}>
            <label style={S.label}>Total Parts Needed</label>
            <input type="number" min="1" value={item.partsNeeded || ""} onChange={(e) => update("partsNeeded", Math.max(0, parseInt(e.target.value) || 0))} placeholder="e.g. 50" style={S.input} />
          </div>
        </div>
        {matched && totalWeight > 0 && (
          <div style={S.lineResultBar}>
            <div style={S.lineResultItem}>
              <span style={{ color: "rgba(10,10,15,0.58)", fontSize: 13, textTransform: "uppercase", letterSpacing: 1 }}>Total Weight</span>
              <span style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, fontSize: 18, color: "#0A0A0F" }}>{totalWeight.toFixed(0)}g</span>
            </div>
            <div style={{ width: 1, height: 28, background: "#F4F2EC" }} />
            <div style={S.lineResultItem}>
              <span style={{ color: "rgba(10,10,15,0.58)", fontSize: 13, textTransform: "uppercase", letterSpacing: 1 }}>Spools</span>
              <span style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, fontSize: 18, color: "#0A0A0F" }}>{spoolsNeeded}</span>
            </div>
            <div style={{ width: 1, height: 28, background: "#F4F2EC" }} />
            <div style={S.lineResultItem}>
              <span style={{ color: "rgba(10,10,15,0.58)", fontSize: 13, textTransform: "uppercase", letterSpacing: 1 }}>Cost/Part</span>
              <span style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, fontSize: 18, color: "#0A0A0F" }}>
                ${partsNeeded > 0 ? (lineCost / partsNeeded).toFixed(2) : "0.00"}
              </span>
            </div>
            <div style={{ width: 1, height: 28, background: "#F4F2EC" }} />
            <div style={S.lineResultItem}>
              <span style={{ color: "rgba(10,10,15,0.58)", fontSize: 13, textTransform: "uppercase", letterSpacing: 1 }}>Line Total</span>
              <span style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, fontSize: 18, color: "#0033FF" }}>
                ${lineCost.toFixed(2)}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
